import { supabase } from '../../../services/supabase/client'
import type { Database } from '../../../types/database.types'

export type ArboristProfile = Database['public']['Tables']['arborist_profiles']['Row']
export type ArboristProfilePayload = Omit<
  Database['public']['Tables']['arborist_profiles']['Insert'],
  'id' | 'created_at' | 'updated_at'
>

/** users.first_name/last_name — every name a report prints comes from here */
export interface ArboristName {
  first_name: string | null
  last_name: string | null
}

class ArboristProfileService {
  async fetchProfile(userId: string): Promise<ArboristProfile | null> {
    const { data, error } = await supabase
      .from('arborist_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()
    if (error) throw new Error(`Failed to fetch arborist profile: ${error.message}`)
    return data
  }

  async upsertProfile(userId: string, payload: ArboristProfilePayload): Promise<ArboristProfile> {
    const { data, error } = await supabase
      .from('arborist_profiles')
      .upsert({ ...payload, id: userId }, { onConflict: 'id' })
      .select()
      .single()
    if (error) throw new Error(`Failed to save arborist profile: ${error.message}`)
    return data
  }

  async fetchName(userId: string): Promise<ArboristName> {
    const { data, error } = await supabase
      .from('users')
      .select('first_name, last_name')
      .eq('id', userId)
      .maybeSingle()
    if (error) throw new Error(`Failed to fetch your name: ${error.message}`)
    return { first_name: data?.first_name ?? null, last_name: data?.last_name ?? null }
  }

  /** Via RPC: users has no self-UPDATE policy (the row also holds role) */
  async updateName(name: ArboristName): Promise<ArboristName> {
    const { data, error } = await supabase
      .rpc('update_my_name', {
        p_first_name: name.first_name ?? '',
        p_last_name: name.last_name ?? '',
      })
      .single()
    if (error) throw new Error(`Failed to save your name: ${error.message}`)
    return { first_name: data.first_name, last_name: data.last_name }
  }
}

export const arboristProfileService = new ArboristProfileService()
